import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedDashboard } from 'reduxStore/app/selectors';
import { zezamiiAccess, zezamiiLockers } from 'theme/brandTokens';
import { Flex, Typography } from 'zui/Antd';

const { Text } = Typography;

// Lead-dot colour per dashboard; anything not listed is Access blue.
const ACCENT_BY_DASHBOARD = { lockers: zezamiiLockers.lockers2 };
const DEFAULT_ACCENT = zezamiiAccess.access3;
const CYCLE_MS = 2800;
const CYCLE = `${CYCLE_MS}ms`;
const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';
const RADIUS = 46;
const ORBIT_SIZE = 2 * RADIUS + 48;
// White followers: how far behind the accent each sits when the ring is open (deg), and its size.
const FOLLOWERS = [1, 2, 3, 4, 5, 6, 7].map((i) => ({ lag: i * 45, size: 10 - i * 0.5, delay: i * 70 }));

// CSS keyframes on transform/opacity only: they run on the compositor, so the orbit stays smooth while the
// main thread is busy bootstrapping (a JS-driven loop would stall exactly then).
const KEYFRAMES = `
	@keyframes dlOrbit { to { transform: rotate(360deg); } }
	@keyframes dlFollow {
		0%, 100% { transform: rotate(var(--lag)); opacity: 1; }
		45% { transform: rotate(0deg); opacity: 1; }
		52%, 62% { transform: rotate(0deg); opacity: 0; }
	}
	@keyframes dlHalo {
		0%, 38% { transform: scale(0.6); opacity: 0; }
		52% { transform: scale(0.85); opacity: 0.9; }
		100% { transform: scale(1.5); opacity: 0; }
	}
	@keyframes dlFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
	@media (prefers-reduced-motion: reduce) {
		.dl-orbit, .dl-follower, .dl-halo { animation: none !important; }
	}
`;

const centred = (size) => ({
	position: 'absolute',
	left: '50%',
	top: '50%',
	width: size,
	height: size,
	margin: -size / 2
});
const onRing = { position: 'absolute', left: 0, top: 0, transform: `translateY(-${RADIUS}px)` };

// The boot passes through several loaders (auth → profile → access). Each one starts its animations at the
// page clock's position in the cycle, so a hand-off continues the orbit instead of restarting it.
const clockPhase = () => -(performance.now() % CYCLE_MS);

// The last title shown, so a loader mounted without its own (a Suspense fallback, a redirect) keeps it
// instead of flashing an empty screen.
const lastShown = { message: undefined };

// The accent circles the Zezamii mark; the white dots trail it on the same ring, close in one after another
// until only the accent shows, then spread out again.
const Orbit = ({ accent, phase }) => (
	<div style={{ position: 'relative', width: ORBIT_SIZE, height: ORBIT_SIZE }}>
		<div className='dl-orbit' style={{ ...centred(0), animation: `dlOrbit ${CYCLE} linear ${phase}ms infinite` }}>
			{FOLLOWERS.map(({ lag, size, delay }) => (
				<div
					key={lag}
					className='dl-follower'
					style={{
						'position': 'absolute',
						'--lag': `${-lag}deg`,
						'animation': `dlFollow ${CYCLE} ${EASE} ${phase + delay}ms infinite both`
					}}
				>
					<div style={onRing}>
						<div style={{ ...centred(size), borderRadius: '50%', background: '#FFFFFF' }} />
					</div>
				</div>
			))}
			<div style={onRing}>
				<div
					className='dl-halo'
					style={{
						...centred(40),
						boxSizing: 'border-box',
						borderRadius: '50%',
						border: `1px solid ${accent}`,
						animation: `dlHalo ${CYCLE} ease-out ${phase}ms infinite`
					}}
				/>
				<div
					style={{
						...centred(14),
						borderRadius: '50%',
						background: accent,
						boxShadow: `0 0 16px ${accent}80`
					}}
				/>
			</div>
		</div>
		<img src='/favicon.svg' alt='' style={centred(34)} />
	</div>
);

// Full-screen boot loader in the selected dashboard's colour, mark fixed at the screen centre.
// Without `message` it keeps the previous loader's; `children` renders under it.
const DarkLoader = ({ message: ownMessage, children }) => {
	const dashboard = useSelector(selectSelectedDashboard);
	const accent = ACCENT_BY_DASHBOARD[dashboard] ?? DEFAULT_ACCENT;
	const [phase] = useState(clockPhase);
	const [mountMessage] = useState(() => lastShown.message);
	const message = ownMessage ?? lastShown.message;

	useEffect(() => {
		lastShown.message = message;
	}, [message]);

	return (
		<Flex
			vertical
			align='center'
			justify='center'
			style={{ height: '100vh', width: '100%', background: '#18181B', position: 'relative' }}
		>
			<style>{KEYFRAMES}</style>
			<Orbit accent={accent} phase={phase} />
			<Flex
				vertical
				align='center'
				style={{ position: 'absolute', left: 16, right: 16, top: '50%', marginTop: RADIUS + 20 }}
			>
				{message && (
					<Text
						key={message}
						style={{
							color: 'rgba(255,255,255,0.88)',
							fontSize: 15,
							maxWidth: 420,
							textAlign: 'center',
							// A title carried over from the previous loader is already on screen: no fade.
							animation: message === mountMessage ? 'none' : 'dlFadeIn 0.3s ease-out'
						}}
					>
						{message}
					</Text>
				)}
				{children}
			</Flex>
		</Flex>
	);
};

export default DarkLoader;
